import { useState, useEffect, useCallback } from 'react'
import moment, { type Moment } from 'moment'
import { useQueryParam, type QueryParamConfig } from 'use-query-params'
import {
  toStartOfWeek,
  momentToQueryStringDate,
} from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.helpers'
import { type StoreGroupDropdownOption } from 'domains/aggregate-store-performance/components/store-group-dropdown/utils/StoreGroupDropdown.types'
import { ALL_STORES_OPTION_ID } from 'domains/aggregate-store-performance/components/store-group-dropdown/utils/StoreGroupDropdown.constants'
import { type FiltersDropdownOption } from 'domains/aggregate-store-performance/components/filters-dropdown/utils/FiltersDropdown.types'
import { useDataPastCutoffDate } from 'domains/aggregate-store-performance/utils/hooks/useDataPastCutoffDate.hook'

const QueryDateParam: QueryParamConfig<Moment, Moment> = {
  encode(value) {
    return momentToQueryStringDate(value)
  },
  decode(strValue) {
    return moment(strValue)
  },
}

const QueryIsWeekParam: QueryParamConfig<boolean, boolean> = {
  encode(value) {
    return value.toString()
  },
  decode(strValue) {
    return strValue === 'true'
  },
}

const QueryIdsParam: QueryParamConfig<string[], string[]> = {
  encode(value) {
    return value.toString()
  },
  decode(strValue: string) {
    return strValue.split(',')
  },
}

export const useStartDate = (now: Moment) => {
  const dataPastCutoffDate = useDataPastCutoffDate()
  const queryKey = 'date'

  const queryParams = new URLSearchParams(window.location.search)
  const initialQueryStartDate = queryParams.get(queryKey)

  const initialDate = initialQueryStartDate ? moment(initialQueryStartDate) : toStartOfWeek(now)

  const [startDate, setStateStartDate] = useState<Moment>(
    initialDate.isBefore(dataPastCutoffDate, 'day') ? dataPastCutoffDate : initialDate
  )
  const [queryStartDate, setQueryStartDate] = useQueryParam(queryKey, QueryDateParam)

  // set initial value
  useEffect(() => {
    setQueryStartDate(startDate, 'replaceIn')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // read from url changes
  useEffect(() => {
    if (queryStartDate !== undefined) {
      setStateStartDate(queryStartDate)
    }
  }, [queryStartDate])

  const setStartDate = useCallback(
    (value: Moment) => {
      const valueToSet = value.isBefore(dataPastCutoffDate, 'day') ? dataPastCutoffDate : value

      if (QueryDateParam.encode(valueToSet) !== QueryDateParam.encode(startDate)) {
        setStateStartDate(valueToSet)
        setQueryStartDate(valueToSet, 'pushIn')
      }
    },
    [dataPastCutoffDate, setQueryStartDate, startDate]
  )

  return { startDate, setStartDate }
}

export const useIsWeek = () => {
  const queryKey = 'week'

  const queryParams = new URLSearchParams(window.location.search)
  const initialQueryWeek = queryParams.get(queryKey)

  const initialWeek = initialQueryWeek ? initialQueryWeek === 'true' : true

  const [isWeek, setStateIsWeek] = useState<boolean>(initialWeek)
  const [queryIsWeek, setQueryIsWeek] = useQueryParam(queryKey, QueryIsWeekParam)

  // set initial value
  useEffect(() => {
    setQueryIsWeek(isWeek, 'replaceIn')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // read from url changes
  useEffect(() => {
    if (queryIsWeek !== undefined) {
      setStateIsWeek(queryIsWeek)
    }
  }, [queryIsWeek])

  const setIsWeek = useCallback(
    (value: boolean) => {
      if (QueryIsWeekParam.encode(value) !== QueryIsWeekParam.encode(isWeek)) {
        setStateIsWeek(value)
        setQueryIsWeek(value, 'pushIn')
      }
    },
    [isWeek, setQueryIsWeek]
  )

  return { isWeek, setIsWeek }
}

export const useSelectedStoreGroups = (
  storeGroups: StoreGroupDropdownOption[],
  isLoading: boolean
) => {
  const queryKey = 'storeGroupIds'

  const [selectedStoreGroups, setStateSelectedStoreGroups] = useState<StoreGroupDropdownOption[]>(
    []
  )
  const [querySelectedStoreGroups, setQuerySelectedStoreGroups] = useQueryParam(
    queryKey,
    QueryIdsParam
  )

  // Change store groups on load of store groups
  useEffect(() => {
    if (!isLoading && storeGroups?.length) {
      if (querySelectedStoreGroups) {
        const selectedStoreGroupsFromQuery = storeGroups.filter(storeGroup =>
          querySelectedStoreGroups.includes(storeGroup.value)
        )
        setStateSelectedStoreGroups(selectedStoreGroupsFromQuery)
      } else {
        const ALL_STORES_OPTION = storeGroups.filter(
          storeGroup => storeGroup.value === ALL_STORES_OPTION_ID
        )
        setQuerySelectedStoreGroups([ALL_STORES_OPTION[0].value], 'replaceIn')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  // read from url changes
  useEffect(() => {
    if (querySelectedStoreGroups !== undefined) {
      setStateSelectedStoreGroups(
        storeGroups.filter(storeGroup => querySelectedStoreGroups.includes(storeGroup.value))
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySelectedStoreGroups])

  const setSelectedStoreGroups = useCallback(
    (newSelectedStoreGroups: StoreGroupDropdownOption[]) => {
      if (
        QueryIdsParam.encode(newSelectedStoreGroups.map(storeGroup => storeGroup.value)) !==
        QueryIdsParam.encode(selectedStoreGroups.map(storeGroup => storeGroup.value))
      ) {
        setStateSelectedStoreGroups(newSelectedStoreGroups)
        setQuerySelectedStoreGroups(
          newSelectedStoreGroups.map(storeGroup => storeGroup.value),
          'pushIn'
        )
      }
    },
    [selectedStoreGroups, setQuerySelectedStoreGroups]
  )

  return {
    selectedStoreGroups,
    setSelectedStoreGroups,
  }
}

export const useSelectedFilters = (filters: FiltersDropdownOption[]) => {
  const queryKey = 'filters'
  const [selectedFilters, setStateSelectedFilters] = useState<FiltersDropdownOption[]>([])
  const [querySelectedFilters, setQuerySelectedFilters] = useQueryParam(queryKey, QueryIdsParam)

  // read from url changes
  useEffect(() => {
    if (querySelectedFilters) {
      setStateSelectedFilters(filters.filter(filter => querySelectedFilters.includes(filter.value)))
    } else {
      setStateSelectedFilters([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySelectedFilters])

  const setSelectedFilters = useCallback(
    (newSelectedFilters: FiltersDropdownOption[]) => {
      if (
        QueryIdsParam.encode(newSelectedFilters.map(filter => filter.value)) !==
        QueryIdsParam.encode(selectedFilters.map(filter => filter.value))
      ) {
        setStateSelectedFilters(newSelectedFilters)
        setQuerySelectedFilters(
          newSelectedFilters.map(filter => filter.value),
          'pushIn'
        )
      }
    },
    [selectedFilters, setQuerySelectedFilters]
  )

  return {
    selectedFilters,
    setSelectedFilters,
  }
}
